<template>
  <div v-if="!loading">
    <navigation-bar>
      <div class="container">
        <div class="row">
          <div class="col-xs-6">
            <uiv-btn type="ghost" class="pull-left" @click="onBack">
              <span class="glyphicon glyphicon-chevron-left"></span>
              Back
            </uiv-btn>

            <div class="logo hidden-xs"></div>
          </div>

          <div class="col-xs-6 text-right">
            <uiv-btn type="primary" @click="onProceed" v-if="quote.switchOption !== 'None'">Switch Now</uiv-btn>
            <span class="badge _margin-top-20" v-if="quote.switchOption == 'None'">Review Only</span>
          </div>
        </div>
      </div>
    </navigation-bar>

    <div class="container _margin-top-50">
      <div class="row _margin-top-10">
        <div class="_margin-top-10 text-right">
          <contact-us />
        </div>
        <div class="col-md-12">
          <h4>Tariff Details</h4>

          <div class="card">
            <div class="row">
              <div class="col-md-4">
                <p class="_margin-top-0 _margin-bottom-20">{{ tariffDetails.supplierNameSimple }}</p>
                <div class="_margin-bottom-20" :class="`company-logo supplier-${tariffDetails.supplierId}`"></div>

                <div class="badge-group">
                  <span class="badge" v-if="tariffDetails.isGreen">Green</span>
                  <span class="badge" v-if="tariffDetails.hasCancellationFee">Exit Fee</span>
                  <span class="badge" v-if="tariffDetails.isFixed">Fixed</span>
                  <span class="badge" v-if="tariffDetails.hasPaperBilling">Paper Billing</span>
                  <span class="badge" v-if="tariffDetails.hasOnlineManagement">Paperless Billing</span>
                  <span class="badge">{{ tariffDetails.hasWarmHomeDiscount ? 'Warm Home Discount' : 'No Warm Home
                    Discount' }}</span>
                </div>
              </div>

              <div class="col-md-6">
                <h6>Features</h6>
                <ul class="bullet-points">
                  <li v-for="(item, index) in limitTo(tariffDetails.tariffDetails)" :key="index">
                    {{ item }}
                  </li>
                </ul>
                <small v-if="tariffDetails.tariffDetails.length === 0">No Tariff Details</small>
                <div class="text-right _margin-top-10" v-if="tariffDetails.tariffDetails.length > 4">
                  <uiv-btn type="default" size="xs" @click="toggleOpen('moreTariffDetails')">
                    {{ tariffDetails.tariffDetails.length - 4 }} {{ moreTariffDetails ? 'less' : 'more' }}
                  </uiv-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h4>Personal Projection</h4>
      <div class="row">
        <div class="col-md-6">
          <div class="card _margin-bottom-30">
            <div class="row">
              <div class="col-xs-6">
                <h5>Existing</h5>
              </div>

              <div class="col-xs-6 text-right">
                <uiv-btn type="default" size="sm" @click="toggleOpen('showMore')">{{ showMore ? 'Less' : 'More' }}
                  Info</uiv-btn>
              </div>
            </div>

            <small class="helper-text _margin-bottom-20">Annual Cost assuming that you take no positive action in the
              next 12 months to change either your tariff or your supplier</small>

            <ppp-table :show-more="showMore" :gas-data="existingProjectionGas"
              :electricity-data="existingProjectionElectricity" :standard-tariff-gas="standardTariffGas"
              :standard-tariff-electricity="standardTariffElectricity">
            </ppp-table>

            <h5 class="hr-top _margin-top-0" v-if="standardTariffElectricity || standardTariffGas">Standard</h5>

            <standard-tariff-table v-if="standardTariffElectricity || standardTariffGas" :show-more="showMore"
              :standard-tariff-gas="standardTariffGas" :standard-tariff-electricity="standardTariffElectricity">
            </standard-tariff-table>

            <h5 class="hr-top _margin-top-0" v-if="standardTariffElectricity || standardTariffGas">Personal Projection
            </h5>

            <table class="table" v-if="standardTariffElectricity || standardTariffGas">
              <tbody>
                <tr>
                  <td class="table-label">Energy</td>
                  <td colspan="1" v-if="standardTariffGas">Gas</td>
                  <td v-if="standardTariffElectricity">Electricity</td>
                </tr>

                <tr>
                  <td class="table-label" style="width:40%;">Total cost of remaining fixed price tariff and standard
                    variable tariff</td>
                  <td colspan="1" v-if="standardTariffGas"><strong>{{ (standardTariffGas.personalProjection +
                    existingProjectionGas.personalProjection) | currency(2) }}</strong></td>
                  <td v-if="standardTariffElectricity"><strong>{{ (standardTariffElectricity.personalProjection +
                    existingProjectionElectricity.personalProjection) | currency(2) }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-md-6">
          <div class="card">
            <div class="row">
              <div class="col-xs-6">
                <h5>New</h5>
              </div>

              <div class="col-xs-6 text-right">
                <uiv-btn type="default" size="sm" @click="toggleOpen('showMore')">{{ showMore ? 'Less' : 'More' }}
                  Info</uiv-btn>
              </div>
            </div>

            <small class="helper-text _margin-bottom-20">Annual Cost if you switch to this tariff - (Your New Personal
              Projection)</small>

            <ppp-table :show-more="showMore" :new-tariff="true" :gas-data="newProjectionGas"
              :electricity-data="newProjectionElectricity">
            </ppp-table>
          </div>
        </div>
      </div>

      <h4 class="_margin-top-30">Tariff Information Label</h4>
      <div class="row">
        <div class="col-md-6">
          <div class="card">
            <h5>Existing</h5>

            <til-table :gas-data="existingTilGas" :electricity-data="existingTilElectricity">
            </til-table>
          </div>
        </div>

        <div class="col-md-6">
          <div class="card">
            <h5>New</h5>

            <til-table :gas-data="newTilGas" :electricity-data="newTilElectricity">
            </til-table>
          </div>
        </div>
      </div>

      <div class="row _margin-top-30 _margin-bottom-30">
        <div class="col-md-12">
          <div class="card">
            <h5>About {{ tariffDetails.supplierNameSimple }}</h5>
            <p>{{ tariffDetails.supplierDescription }}</p>

            <div class="text-right">
              <uiv-btn type="primary" v-if="tariffDetails.termsAndConditionsUrlDto"
                @click="onTermsConditions(tariffDetails.termsAndConditionsUrlDto.supplier)">View Terms &
                Conditions</uiv-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavigationBar from '@/components/navigation-bar/NavigationBar';
import PppTable from '@/components/tariff-details/PppTable';
import StandardTariffTable from '@/components/tariff-details/StandardTariffTable';
import TilTable from '@/components/tariff-details/TilTable';
import ContactUs from '@/components/contact-us/ContactUs';

import { mapGetters, mapState } from 'vuex';

export default {
  name: 'TariffDetails',
  data() {
    return {
      showMore: false,
      moreTariffDetails: false
    };
  },
  async mounted() {
    if (this.$route.params.tariffId !== this.tariffDetails.tariffId) {
      this.$store.dispatch('tariff_details/getTariffDetails', this.$route.params.tariffId);
    }
  },
  computed: {
    ...mapState({
      quote: ({ storage }) => storage.quote,
      loading: ({ page_loader }) => page_loader.show,
      tariffDetails: ({ tariff_details }) => tariff_details.tariffDetails,
      existingProjectionElectricity: ({ tariff_details }) => {
        const { electricityChosenPersonalProjectionPlan } = tariff_details.existingProjection;

        return electricityChosenPersonalProjectionPlan;
      },
      standardTariffElectricity: ({ tariff_details }) => {
        const { electricityDefaultPersonalProjectionPlan } = tariff_details.existingProjection;

        return electricityDefaultPersonalProjectionPlan;
      },
      existingProjectionGas: ({ tariff_details }) => {
        const { gasChosenPersonalProjectionPlan } = tariff_details.existingProjection;

        return gasChosenPersonalProjectionPlan;
      },
      standardTariffGas: ({ tariff_details }) => {
        const { gasDefaultPersonalProjectionPlan } = tariff_details.existingProjection;

        return gasDefaultPersonalProjectionPlan;
      },
      newProjectionElectricity: ({ tariff_details }) => {
        const { electricityChosenPersonalProjectionPlan } = tariff_details.newProjection;

        return electricityChosenPersonalProjectionPlan;
      },
      newProjectionGas: ({ tariff_details }) => {
        const { gasChosenPersonalProjectionPlan } = tariff_details.newProjection;

        return gasChosenPersonalProjectionPlan;
      },
      existingTilElectricity: ({ tariff_details }) => {
        const { electricityTariffInformationLabel } = tariff_details.existingTil;

        return electricityTariffInformationLabel;
      },
      existingTilGas: ({ tariff_details }) => {
        const { gasTariffInformationLabel } = tariff_details.existingTil;

        return gasTariffInformationLabel;
      },
      newTilElectricity: ({ tariff_details }) => {
        const { electricityTariffInformationLabel } = tariff_details.newTil;

        return electricityTariffInformationLabel;
      },
      newTilGas: ({ tariff_details }) => {
        const { gasTariffInformationLabel } = tariff_details.newTil;

        return gasTariffInformationLabel;
      }
    })
  },
  methods: {
    async onBack() {
      this.$store.dispatch('tracing/addAction', { name: 'Page', value: 'Tariff Details' });
      this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Back' });
      await this.$store.dispatch('tracing/saveActions');
      this.$router.go(-1);
    },
    async onProceed() {
      this.$store.dispatch('tracing/addAction', { name: 'Page', value: 'Tariff Details' });
      this.$store.dispatch('tracing/addAction', { name: 'Action', value: 'Switch Now' });
      this.$store.dispatch('tracing/addAction', { name: 'Tariff Id', value: this.$route.params.tariffId });
      await this.$store.dispatch('tracing/saveActions');
      await this.$store.dispatch('quote_results/switchNow', {
        tariffId: this.$route.params.tariffId
      });
    },
    async toggleOpen(prop) {
      this[prop] = !this[prop];
    },
    async onTermsConditions(url) {
      window.open(url);
    },
    limitTo(array) {
      if (!this.moreTariffDetails) {
        return array.slice(0, 4);
      }

      return array;
    }
  },
  components: {
    NavigationBar,
    PppTable,
    StandardTariffTable,
    TilTable,
    ContactUs
  }
};
</script>

<style lang="scss" scoped>
.helper-text {
  display: block;
  min-height: 40px;
}

.badge-group {
  .badge {
    margin: 0 4px 6px 0;
  }
}

.logo {
  margin: 10px 0 0 20px;
  height: 37px;
  width: 165px;
  background: url('../../assets/logo.png') no-repeat;
  background-size: contain;
  display: inline-block;
}

.navbar-default .btn {
  margin-top: 8px;
}

.hr-top {
  padding-top: 10px;
  border-top: 1px solid #eee;
}
</style>
